import { Button } from "@/components/ui/button";
import { ExternalLink, Monitor, Smartphone, Tablet } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useRef } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";

const ShowcaseSection = () => {
  const { t } = useLanguage();
  const autoplayRef = useRef(
    Autoplay({
      delay: 4000,
      stopOnInteraction: true,
      stopOnMouseEnter: true,
    })
  );

  const showcaseWebsites = [
    {
      title: "Octopsen",
      description: "Pomáhame firmám prerásť svojho zakladateľa - stabilný systém, ktorý funguje aj bez teba",
      image: "/lovable-uploads/octopsen-preview.png",
      url: "https://octopsen.com",
      tags: ["Business", "Automation", "Consulting"]
    },
    {
      title: "FVEnergy",
      description: "Automatizované riešenie pre OKTE reporting fotovoltických výrobní",
      image: "/lovable-uploads/fvenergy-preview.png",
      url: "https://fvenergy.sk",
      tags: ["Energy", "Automation", "OKTE"]
    },
    {
      title: "Wait What Agency",
      description: "Creative branding agency creating brands with unique character and story",
      image: "/lovable-uploads/waitwhat-preview.png",
      url: "https://waitwhat.lovable.app",
      tags: ["Branding", "Creative", "Design Agency"]
    },
    {
      title: "Vissionary UGC",
      description: "High-performing UGC videos created with AI at scale for marketing teams",
      image: "/lovable-uploads/vissionaryugc-preview.png",
      url: "https://vissionaryugc.com",
      tags: ["UGC", "AI Video", "Marketing"]
    },
    {
      title: "Vissionary AI",
      description: "AI Partner for innovative businesses and digital transformation projects",
      image: "/lovable-uploads/vissionaryai-preview.png",
      url: "https://www.vissionaryai.com",
      tags: ["AI Partner", "Innovation", "Digital"]
    },
    {
      title: "Awaken Ambiance",
      description: "Real-time voice AI that responds naturally to voice commands and conversations",
      image: "/lovable-uploads/ef3fc23e-5495-49e6-af27-383348df3b61.png",
      url: "https://preview--awaken-galaxy-voice-57.lovable.app/",
      tags: ["Voice AI", "Real-time", "Interactive"]
    },
    {
      title: "LOOK AI",
      description: "Real-Time AI Fashion Design Tool for innovative clothing design and creativity",
      image: "/lovable-uploads/91e4304b-7cd1-44fb-b1d9-77818added7c.png",
      url: "https://lookfashion.ai/",
      tags: ["AI Fashion", "Design Tool", "Creative"]
    },
    {
      title: "Aneta",
      description: "AI agents to supercharge HR and workplace engagement for modern teams",
      image: "/lovable-uploads/4ce19ff5-77ec-4767-b2b0-2590269793b9.png",
      url: "https://www.aneta.so/",
      tags: ["HR Tech", "AI Agents", "Team Engagement"]
    },
    {
      title: "Pulse Robot",
      description: "Interactive robot animation and modern web design showcase",
      image: "/lovable-uploads/pulse-robot-preview.png",
      url: "https://pulse-robot-template.lovable.app",
      tags: ["Animation", "Interactive", "Modern Design"]
    }
  ];

  return (
    <section id="showcase" className="py-20 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-outline text-primary mb-6 uppercase" style={{ fontWeight: 900 }}>
            <span className="relative inline-block">
              <span className="relative z-10">{t('showcase.title').split(' ').slice(0, 3).join(' ')}</span>
              <span 
                className="absolute bottom-2 left-0 right-0 h-4 -rotate-1 -z-0"
                style={{
                  backgroundColor: 'hsl(11, 86%, 55%)',
                  background: 'linear-gradient(104deg, rgba(239, 106, 55, 0) 0.9%, rgba(239, 106, 55, 1) 2.4%, rgba(239, 106, 55, 0.6) 5.8%, rgba(239, 106, 55, 0.1) 93%, rgba(239, 106, 55, 0.8) 96%, rgba(239, 106, 55, 0) 98%), linear-gradient(183deg, rgba(239, 106, 55, 0) 0%, rgba(239, 106, 55, 0.3) 7.9%, rgba(239, 106, 55, 0) 15%)'
                }}
              />
            </span>{' '}
            {t('showcase.title').split(' ').slice(3).join(' ')}
          </h2>
          <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto font-medium text-center mb-4">
            {t('showcase.subtitle')}
          </p>
          <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
            {t('showcase.description')}
          </p>
        </div>

        {/* Showcase Carousel */}
        <Carousel
          opts={{
            align: "start",
            loop: true,
            skipSnaps: false,
            dragFree: false,
          }}
          plugins={[autoplayRef.current]}
          className="w-full"
        >
          <CarouselContent className="-ml-4">
            {showcaseWebsites.map((website, index) => (
              <CarouselItem key={index} className="pl-4 md:basis-1/2 lg:basis-1/3 xl:basis-1/4" style={{ contain: 'layout' }}>
                <div className="group bg-card rounded-2xl overflow-hidden shadow-elegant border border-border/50 transition-all duration-300 hover:shadow-accent hover:border-accent/30 hover:scale-105 h-full will-change-transform">
                  {/* Website Preview */}
                  <div className="relative overflow-hidden">
                    <img 
                      src={website.image} 
                      alt={website.title}
                      className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    
                    {/* Device Icons Overlay */}
                    <div className="absolute top-4 right-4 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="bg-white/90 p-2 rounded-lg">
                        <Monitor className="w-4 h-4 text-primary" />
                      </div>
                      <div className="bg-white/90 p-2 rounded-lg">
                        <Tablet className="w-4 h-4 text-primary" />
                      </div>
                      <div className="bg-white/90 p-2 rounded-lg">
                        <Smartphone className="w-4 h-4 text-primary" />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-display font-bold text-card-foreground mb-3 uppercase">
                      {website.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                      {website.description}
                    </p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {website.tags.map((tag) => (
                        <span 
                          key={tag}
                          className="px-2 py-1 bg-accent/10 text-accent text-xs rounded-full border border-accent/20"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* CTA Button */}
                    <Button 
                      variant="outline"
                      size="sm"
                      className="w-full group/btn transition-all duration-300 hover:bg-accent hover:text-accent-foreground hover:border-accent"
                      onClick={() => window.open(website.url, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-2 transition-transform duration-300 group-hover/btn:scale-110" />
                      {t('showcase.visitWebsite')}
                    </Button>
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="hidden md:flex -left-12 bg-card border-accent/50 hover:bg-accent hover:text-accent-foreground" />
          <CarouselNext className="hidden md:flex -right-12 bg-card border-accent/50 hover:bg-accent hover:text-accent-foreground" />
        </Carousel>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-accent/10 via-primary/10 to-accent/10 border border-accent/20 rounded-2xl p-8">
            <h3 className="text-2xl font-display font-bold text-primary mb-4 uppercase">
              {t('showcase.ctaTitle')}
            </h3>
            <p className="text-muted-foreground mb-6">
              {t('showcase.ctaDescription')}
            </p>
            <Button 
              size="lg"
              className="bg-accent hover:bg-accent/90 text-accent-foreground transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-accent/30"
              onClick={() => {
                const element = document.querySelector("#contact");
                if (element) {
                  element.scrollIntoView({ behavior: "smooth" });
                }
              }}
            >
              {t('showcase.ctaButton')}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ShowcaseSection;